import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibraryAttendanceParams } from '../../../services/api/dataLibrary';
import type { DataLibraryAttendanceRow, DataLibraryAttendanceFilterOptions, ColumnAverages } from '../../../types';
import type { SortConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, normalizeGroupState, type GroupNode } from '../../../utils/groupDataByField';
import { ATTENDANCE_COLUMNS } from './attendanceColumns';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

interface UseDataLibraryAttendanceProps {
  selectedCohortIds: string[];
  /** MED-661: Initial group-by state when opened from drill-down */
  initialGroup?: GroupState | null;
  /** MED-773: Initial filters when opened from drill-down with group selection */
  initialFilters?: ActiveFilterRow[];
}

export function useDataLibraryAttendance({ selectedCohortIds, initialGroup, initialFilters }: UseDataLibraryAttendanceProps) {
  // Data state
  const [data, setData] = useState<DataLibraryAttendanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // MDF-41: server averages over all matching rows, by column id
  const [averages, setAverages] = useState<ColumnAverages>({});
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibraryAttendanceFilterOptions>({
    groups: [],
    coaches: [],
    sessionNumbers: [],
    departments: [],
    locations: [],
    businessUnits: [],
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Search
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort (server-side) — default: session_number DESC
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: 'session_number',
    order: 'DESC',
  });

  // Filters
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>(initialFilters ?? []);

  // Grouping (client-side)
  const [activeGroup, setActiveGroup] = useState<GroupState | null>(() => normalizeGroupState(initialGroup));

  // Column config
  const { columns, setColumns } = useDataLibraryColumns(ATTENDANCE_COLUMNS);
  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  // Build API params from state
  const buildParams = useCallback((): DataLibraryAttendanceParams => {
    const params: DataLibraryAttendanceParams = {
      cohortIds: selectedCohortIds,
      page,
      limit: pageSize,
      ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
    };

    if (debouncedSearch) params.search = debouncedSearch;

    // Quick filters
    if (activeQuickFilters.includes('absent')) params.absentOnly = true;
    if (activeQuickFilters.includes('unexcused')) params.unexcusedOnly = true;

    // Active filters
    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      switch (filter.key) {
        case 'status':
          params.status = filter.values[0];
          break;
        case 'group':
          params.groupId = filter.values.join(',');
          break;
        case 'coach':
          params.coachId = filter.values[0];
          break;
        case 'session_number':
          params.sessionNumber = filter.values.join(',');
          break;
        case 'attendance':
          params.attendanceStatus = filter.values.join(',');
          break;
        case 'department':
          params.department = filter.values[0];
          break;
        case 'location':
          params.location = filter.values[0];
          break;
        case 'business_unit':
          params.businessUnit = filter.values[0];
          break;
      }
    }

    return params;
  }, [
    selectedCohortIds,
    page,
    pageSize,
    sortConfig,
    debouncedSearch,
    activeQuickFilters,
    activeFilters,
  ]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  // Fetch data
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setData([]);
      setTotalCount(0);
      setAverages({});
      setTotalPages(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages((page, limit) => dataLibraryApi.getAttendance({ ...params, page, limit }), () => requestId !== latestRequest.current)
        : await dataLibraryApi.getAttendance(params);
      if (requestId !== latestRequest.current) return;
      const total = result.meta.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setData(result.data || []);
      setTotalCount(result.meta.pagination.total);
      setAverages(result.meta.averages);
      setTotalPages(result.meta.pagination.totalPages);
      setFilterOptions(result.meta.filterOptions);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load attendance data',
        color: 'red',
      });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, buildParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeQuickFilters, activeFilters, selectedCohortIds]);

  // Sort handlers
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortConfig({ key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' });
    } else {
      setSortConfig(null);
    }
  }, []);

  // Quick filter toggle
  const handleQuickFilterToggle = useCallback((key: string) => {
    setActiveQuickFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  }, []);

  // Filter handlers
  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, { key, values: [] }];
    });
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveQuickFilters([]);
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters =
    activeQuickFilters.length > 0 ||
    activeFilters.some(f => f.values.length > 0) ||
    debouncedSearch.length > 0;

  // Grouped data (client-side grouping)
  const groupedData = useMemo(() => {
    return groupDataByLevels(
      data as unknown as Record<string, unknown>[],
      isGrouped ? activeGroup : null,
      columns,
    ) as GroupNode<DataLibraryAttendanceRow>[] | null;
  }, [data, activeGroup, isGrouped, columns]);

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    data,
    groupedData,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,

    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },

    searchValue,
    setSearchValue,

    sortConfig,
    handleSort,
    handleSortPanelChange,

    activeQuickFilters,
    handleQuickFilterToggle,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,

    activeGroup,
    setActiveGroup,

    columns,
    setColumns,
    visibleColumns,

    fetchData,
  };
}
